import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import * as yup from "yup";
import { useFormik } from "formik";
import { Icons } from "assets";
import CustomButton from "shared/components/customButton";
import Loader from "shared/components/loader/animation";
import { toastMessage } from "shared/components/toast";
import { reviewDemoRequestService } from "shared/services/adminService";
import styles from "./style.module.scss";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface Props {
  open: boolean;
  handleClose: () => void;
  selectedItem: any;
  onSuccess: () => void;
}

export default function EditDemoRequest({
  open,
  handleClose,
  selectedItem,
  onSuccess,
}: Props) {
  const [loader, setLoader] = useState<boolean>(false);

  const isMultiCampus = Boolean(selectedItem?.is_multi_campus);

  const validationSchema = yup.object().shape({
    status: yup.string().required("Status is required"),
    students_count: yup
      .number()
      .typeError("Must be a valid number")
      .required("Students count is required")
      .min(0, "Cannot be negative"),
    teachers_count: yup
      .number()
      .typeError("Must be a valid number")
      .required("Teachers count is required")
      .min(0, "Cannot be negative"),
    campuses_count: isMultiCampus
      ? yup
          .number()
          .typeError("Must be a valid number")
          .required("Number of campuses is required")
          .min(1, "Must be at least 1")
      : yup.number().optional(),
    notes: yup.string().optional(),
    rejection_reason: yup.string().when("status", {
      is: "3",
      then: (schema) => schema.required("Reject reason is required"),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      status: "",
      students_count: selectedItem?.students_count ?? "",
      teachers_count: selectedItem?.teachers_count ?? "",
      campuses_count: selectedItem?.campuses_count ?? "",
      notes: selectedItem?.notes ?? "",
      rejection_reason: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("status", values.status);
      formData.append("students_count", values.students_count.toString());
      formData.append("teachers_count", values.teachers_count.toString());
      if (isMultiCampus)
        formData.append("campuses_count", values.campuses_count.toString());
      if (values.notes) formData.append("notes", values.notes);
      if (values.rejection_reason)
        formData.append("rejection_reason", values.rejection_reason);

      setLoader(true);
      reviewDemoRequestService(selectedItem?.id, formData)
        .then(({ data }) => {
          toastMessage("success", data?.message);
          setLoader(false);
          onClose();
          onSuccess();
        })
        .catch((err) => {
          toastMessage("error", err?.response?.data?.message);
          setLoader(false);
        });
    },
  });

  const onClose = () => {
    formik.resetForm();
    handleClose();
  };

  return (
    <div>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        aria-describedby="edit-demo-request-dialog"
        fullWidth
      >
        <DialogContent>
          <Icons.CrossIcon className={styles.crossIcon} onClick={onClose} />

          <div className={styles.modalTitle}>Review Demo Request</div>

          <form onSubmit={formik.handleSubmit}>
            <div className={styles.twoColRow}>
              <div className={styles.fieldWrapper}>
                <label className={styles.label}>Students Count</label>
                <input
                  type="number"
                  className={styles.inputField}
                  id="students_count"
                  name="students_count"
                  value={formik.values.students_count}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  min={0}
                />
                {formik.touched.students_count &&
                  formik.errors.students_count && (
                    <div className={styles.errorText}>
                      {formik.errors.students_count as string}
                    </div>
                  )}
              </div>

              <div className={styles.fieldWrapper}>
                <label className={styles.label}>Teachers Count</label>
                <input
                  type="number"
                  className={styles.inputField}
                  id="teachers_count"
                  name="teachers_count"
                  value={formik.values.teachers_count}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  min={0}
                />
                {formik.touched.teachers_count &&
                  formik.errors.teachers_count && (
                    <div className={styles.errorText}>
                      {formik.errors.teachers_count as string}
                    </div>
                  )}
              </div>
            </div>

            {isMultiCampus && (
              <div className={styles.fieldWrapper}>
                <label className={styles.label}>No. of Campuses</label>
                <input
                  type="number"
                  className={styles.inputField}
                  id="campuses_count"
                  name="campuses_count"
                  value={formik.values.campuses_count}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  min={1}
                />
                {formik.touched.campuses_count &&
                  formik.errors.campuses_count && (
                    <div className={styles.errorText}>
                      {formik.errors.campuses_count as string}
                    </div>
                  )}
              </div>
            )}

            <div className={styles.fieldWrapper}>
              <label className={styles.label}>Status</label>
              <select
                className={styles.selectField}
                id="status"
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select Status</option>
                <option value="2">Approve</option>
                <option value="3">Reject</option>
              </select>
              {formik.touched.status && formik.errors.status && (
                <div className={styles.errorText}>{formik.errors.status}</div>
              )}
            </div>

            {formik.values.status === "3" && (
              <div className={styles.fieldWrapper}>
                <label className={styles.areaLabel}>Reject Reason</label>
                <textarea
                  className={styles.textarea}
                  id="rejection_reason"
                  name="rejection_reason"
                  value={formik.values.rejection_reason}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter reason for rejection..."
                />
                {formik.touched.rejection_reason && formik.errors.rejection_reason && (
                  <div className={styles.errorText}>
                    {formik.errors.rejection_reason as string}
                  </div>
                )}
              </div>
            )}

            <div className={styles.fieldWrapper}>
              <label className={styles.areaLabel}>Notes</label>
              <textarea
                className={styles.textarea}
                id="notes"
                name="notes"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Add notes here..."
              />
            </div>

            <div className={styles.btnContainer}>
              <CustomButton
                text="Update"
                buttonStyle={styles.btnCustom}
                handleClick={formik.handleSubmit}
              />
              <CustomButton
                text="Cancel"
                buttonStyle={styles.btnInsideOut}
                handleClick={onClose}
              />
            </div>
          </form>
        </DialogContent>

        {loader && <Loader />}
      </Dialog>
    </div>
  );
}
